/**
 * ExecutionController - Manages automatic execution of node chains
 * 
 * This service orchestrates the execution of connected nodes in topological order,
 * supporting full flow execution, partial execution, and parallel execution.
 */

import { Node, Edge } from '@xyflow/react';
import { topologicalSort, TopologicalSortResult } from '../utils/topologicalSort';
import { getNodeByReactFlowType } from '../config/nodeRegistry';

// Execution modes
export type ExecutionMode = 'full' | 'from-node' | 'selected' | 'segment';

// Execution options
export interface ExecutionOptions {
  mode: ExecutionMode;
  startNodeId?: string;
  endNodeId?: string;
  selectedNodeIds?: string[];
  parallel?: boolean;           // Execute independent nodes in parallel (default: false for visual clarity)
  maxConcurrency?: number;      // Max simultaneous executors per level in parallel mode (default: 4)
  stopOnError?: boolean;        // Stop chain if a node fails (default: true)
  delayBetweenNodes?: number;   // ms delay for visual feedback (default: 500)
}

// Node execution status
export type NodeExecutionStatus = 'pending' | 'running' | 'completed' | 'failed' | 'skipped' | 'cancelled';

export interface NodeExecutionState {
  nodeId: string;
  status: NodeExecutionStatus;
  error?: string;
  startedAt?: number;
  completedAt?: number;
}

// Overall execution state
export interface ExecutionState {
  isExecuting: boolean;
  isPaused: boolean;
  isCancelled: boolean;
  currentNodeId: string | null;
  nodeStates: Map<string, NodeExecutionState>;
  completedCount: number;
  totalCount: number;
  progress: number;             // 0-100
  startedAt?: number;
  error?: string;
}

// Executor function type
export type NodeExecutor = () => Promise<void>;

// State change callback
export type ExecutionStateCallback = (state: ExecutionState) => void;

// Default options
const DEFAULT_OPTIONS: Partial<ExecutionOptions> = {
  parallel: false,
  maxConcurrency: 4,
  stopOnError: true,
  delayBetweenNodes: 300,
};

/**
 * ExecutionController singleton
 */
class ExecutionControllerClass {
  // Registered node executors
  private executors: Map<string, NodeExecutor> = new Map();
  
  // Current execution state
  private state: ExecutionState = this.createInitialState();
  
  // State change listeners
  private listeners: Set<ExecutionStateCallback> = new Set();
  
  // Pause/cancel control
  private pausePromise: Promise<void> | null = null;
  private pauseResolve: (() => void) | null = null;
  
  /**
   * Register a node's executor function
   */
  registerExecutor(nodeId: string, executor: NodeExecutor): void {
    this.executors.set(nodeId, executor);
  }
  
  /**
   * Unregister a node's executor (called on unmount)
   */
  unregisterExecutor(nodeId: string): void {
    this.executors.delete(nodeId);
  }
  
  /**
   * Check if a node has a registered executor
   */
  hasExecutor(nodeId: string): boolean {
    return this.executors.has(nodeId);
  }
  
  /**
   * Get current execution state
   */
  getState(): ExecutionState {
    return { ...this.state, nodeStates: new Map(this.state.nodeStates) };
  }
  
  /**
   * Subscribe to state changes
   */
  subscribe(callback: ExecutionStateCallback): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }
  
  /**
   * Execute a flow
   */
  async executeFlow(
    nodes: Node[],
    edges: Edge[],
    options: ExecutionOptions
  ): Promise<void> {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    
    // Prevent concurrent executions
    if (this.state.isExecuting) {
      console.warn('[ExecutionController] Already executing, ignoring request');
      return;
    }
    
    // Get execution order
    let sortResult: TopologicalSortResult;
    
    if (opts.mode === 'selected' && opts.selectedNodeIds) {
      // Filter to only selected nodes
      const selectedNodes = nodes.filter(n => opts.selectedNodeIds!.includes(n.id));
      const relevantEdges = edges.filter(
        e => opts.selectedNodeIds!.includes(e.source) && opts.selectedNodeIds!.includes(e.target)
      );
      sortResult = topologicalSort(selectedNodes, relevantEdges);
    } else {
      sortResult = topologicalSort(nodes, edges, opts.startNodeId, opts.endNodeId);
    }
    
    // Check for cycles
    if (sortResult.hasCycle) {
      this.notifyError(`Cannot execute: cycle detected involving nodes: ${sortResult.cycleNodes.join(', ')}`);
      return;
    }
    
    // Filter to only nodes that have executors
    const executableNodeIds = sortResult.orderedNodeIds.filter(id => this.executors.has(id));

    // Guard against virtualization: executors register on node MOUNT and
    // unregister on unmount, and ReactFlow's onlyRenderVisibleElements
    // unmounts off-screen nodes. A node whose registry entry says it's an
    // executable type but has no executor is off-screen — silently
    // skipping it would run a partial flow, so fail loudly instead.
    // (The structural fix is registering executors flow-level; this is
    // the interim no-silent-fallbacks guard.)
    const missingExecutors = sortResult.orderedNodeIds.filter(id => {
      if (this.executors.has(id)) return false;
      const node = nodes.find(n => n.id === id);
      if (!node?.type) return false;
      const registration = getNodeByReactFlowType(node.type);
      return !!registration && registration.handlerType !== 'none';
    });

    if (missingExecutors.length > 0) {
      const names = missingExecutors
        .map(id => {
          const node = nodes.find(n => n.id === id);
          return (node?.data as any)?.title || node?.type || id;
        })
        .slice(0, 5)
        .join(', ');
      this.notifyError(
        `Cannot run: ${missingExecutors.length} node(s) are off-screen and not ready (${names}${missingExecutors.length > 5 ? ', …' : ''}). ` +
        'Zoom out (or use zoom-to-fit) so all nodes are visible, then run again.'
      );
      return;
    }

    if (executableNodeIds.length === 0) {
      this.notifyError('No executable nodes found');
      return;
    }
    
    // Initialize state
    this.state = this.createInitialState();
    this.state.isExecuting = true;
    this.state.totalCount = executableNodeIds.length;
    this.state.startedAt = Date.now();
    
    // Initialize node states
    executableNodeIds.forEach(nodeId => {
      this.state.nodeStates.set(nodeId, {
        nodeId,
        status: 'pending'
      });
    });
    
    this.notifyListeners();
    
    console.log('[ExecutionController] Starting execution:', {
      mode: opts.mode,
      totalNodes: executableNodeIds.length,
      order: executableNodeIds
    });
    
    try {
      if (opts.parallel) {
        // Execute by levels (parallel within each level)
        for (const level of sortResult.levels) {
          if (this.state.isCancelled) break;
          await this.checkPaused();
          
          const levelNodeIds = level.nodeIds.filter(id => executableNodeIds.includes(id));
          if (levelNodeIds.length === 0) continue;

          await this.runLevelWithLimit(levelNodeIds, opts);
          
          // Check if any failed and we should stop
          if (opts.stopOnError) {
            const failed = levelNodeIds.some(
              id => this.state.nodeStates.get(id)?.status === 'failed'
            );
            if (failed) break;
          }
        }
      } else {
        // Execute sequentially
        for (const nodeId of executableNodeIds) {
          if (this.state.isCancelled) break;
          await this.checkPaused();
          
          const result = await this.executeNode(nodeId, opts);
          
          if (!result && opts.stopOnError) {
            break;
          }
          
          // Delay for visual feedback
          if (opts.delayBetweenNodes && opts.delayBetweenNodes > 0) {
            await this.delay(opts.delayBetweenNodes);
          }
        }
      }
    } catch (error) {
      console.error('[ExecutionController] Execution error:', error);
      this.state.error = error instanceof Error ? error.message : 'Unknown error';
    } finally {
      // Mark remaining pending nodes as cancelled if we stopped early
      if (this.state.isCancelled) {
        this.state.nodeStates.forEach((nodeState, nodeId) => {
          if (nodeState.status === 'pending') {
            this.state.nodeStates.set(nodeId, { ...nodeState, status: 'cancelled' });
          }
        });
      }
      
      this.state.isExecuting = false;
      this.state.currentNodeId = null;
      this.notifyListeners();
      
      console.log('[ExecutionController] Execution complete:', {
        completed: this.state.completedCount,
        total: this.state.totalCount,
        cancelled: this.state.isCancelled
      });
    }
  }
  
  /**
   * Execute a level's nodes in parallel with a concurrency cap
   */
  private async runLevelWithLimit(nodeIds: string[], opts: ExecutionOptions): Promise<void> {
    const limit = Math.max(1, opts.maxConcurrency ?? 4);
    const queue = [...nodeIds];
    const inFlight = new Set<Promise<unknown>>();

    while (queue.length > 0 || inFlight.size > 0) {
      while (inFlight.size < limit && queue.length > 0) {
        const id = queue.shift()!;
        const p = this.executeNode(id, opts).finally(() => {
          inFlight.delete(p);
        });
        inFlight.add(p);
      }
      if (inFlight.size > 0) {
        await Promise.race(inFlight);
      }
      if (this.state.isCancelled) break;
    }

    await Promise.all(inFlight);
  }

  /**
   * Execute a single node
   */
  private async executeNode(nodeId: string, _opts: ExecutionOptions): Promise<boolean> {
    const executor = this.executors.get(nodeId);
    if (!executor) {
      console.warn(`[ExecutionController] No executor for node ${nodeId}`);
      return true; // Skip but continue
    }
    
    // Update state to running
    this.state.currentNodeId = nodeId;
    this.state.nodeStates.set(nodeId, {
      nodeId,
      status: 'running',
      startedAt: Date.now()
    });
    this.notifyListeners();
    
    try {
      await executor();
      
      // Success
      this.state.nodeStates.set(nodeId, {
        nodeId,
        status: 'completed',
        startedAt: this.state.nodeStates.get(nodeId)?.startedAt,
        completedAt: Date.now()
      });
      this.state.completedCount++;
      this.state.progress = Math.round((this.state.completedCount / this.state.totalCount) * 100);
      this.notifyListeners();
      
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error(`[ExecutionController] Node ${nodeId} failed:`, error);
      
      this.state.nodeStates.set(nodeId, {
        nodeId,
        status: 'failed',
        error: errorMessage,
        startedAt: this.state.nodeStates.get(nodeId)?.startedAt,
        completedAt: Date.now()
      });
      this.state.error = `Node failed: ${errorMessage}`;
      this.notifyListeners();
      
      return false;
    }
  }
  
  /**
   * Pause execution
   */
  pause(): void {
    if (!this.state.isExecuting || this.state.isPaused) return;
    
    this.state.isPaused = true;
    this.pausePromise = new Promise(resolve => {
      this.pauseResolve = resolve;
    });
    this.notifyListeners();
    
    console.log('[ExecutionController] Paused');
  }
  
  /**
   * Resume execution
   */
  resume(): void {
    if (!this.state.isPaused) return;
    
    this.state.isPaused = false;
    if (this.pauseResolve) {
      this.pauseResolve();
      this.pauseResolve = null;
      this.pausePromise = null;
    }
    this.notifyListeners();
    
    console.log('[ExecutionController] Resumed');
  }
  
  /**
   * Cancel execution
   */
  cancel(): void {
    if (!this.state.isExecuting) return;
    
    this.state.isCancelled = true;
    
    // Also resume if paused to allow cancellation to proceed
    if (this.state.isPaused) {
      this.resume();
    }
    
    this.notifyListeners();
    
    console.log('[ExecutionController] Cancelled');
  }
  
  /**
   * Reset state (for cleanup)
   */
  reset(): void {
    this.state = this.createInitialState();
    this.notifyListeners();
  }
  
  /**
   * Check if paused and wait
   */
  private async checkPaused(): Promise<void> {
    if (this.pausePromise) {
      await this.pausePromise;
    }
  }
  
  /**
   * Create initial state
   */
  private createInitialState(): ExecutionState {
    return {
      isExecuting: false,
      isPaused: false,
      isCancelled: false,
      currentNodeId: null,
      nodeStates: new Map(),
      completedCount: 0,
      totalCount: 0,
      progress: 0
    };
  }
  
  /**
   * Notify all listeners of state change
   */
  private notifyListeners(): void {
    const stateCopy = this.getState();
    this.listeners.forEach(listener => {
      try {
        listener(stateCopy);
      } catch (error) {
        console.error('[ExecutionController] Listener error:', error);
      }
    });
  }
  
  /**
   * Notify error (when can't start)
   */
  private notifyError(message: string): void {
    console.error('[ExecutionController]', message);
    this.state.error = message;
    this.notifyListeners();
  }
  
  /**
   * Helper delay function
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Export singleton instance
export const executionController = new ExecutionControllerClass();

// Export types
export type { TopologicalSortResult };
